import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RootState } from '../store';
import Header from './Header';
import Footer from './Footer';
import CookieBanner from './CookieBanner';
import flower from '../assets/images/flower_1.png';

// Critical LCP asset loaded from public folder for optimal performance
const clouds = '/assets/images/clouds.webp';

interface BaseLayoutProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
  keywords?: string;
  imageUrl?: string;
}

const BaseLayout: React.FC<BaseLayoutProps> = ({
  children,
  title,
  description,
  keywords,
  imageUrl = '/logo512.png',
}) => {
  const currentLanguage = useSelector((state: RootState) => state.language.currentLanguage);
  const location = useLocation();

  // Calculate daily changing hue value for cloud background
  const today = new Date();
  const startOfYear = new Date(today.getFullYear(), 0, 0);
  const diff = today.getTime() - startOfYear.getTime();
  const oneDay = 1000 * 60 * 60 * 24;
  const dayOfYear = Math.floor(diff / oneDay);
  const hue = dayOfYear % 360;

  // Cloud background scoped to content container (not global)
  const cloudImageStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundImage: `url(${clouds})`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    filter: `hue-rotate(${hue}deg) saturate(70%) brightness(100%)`,
    opacity: 1.0,
    zIndex: 0,
  };

  const getDefaultTitle = (): string => {
    return currentLanguage === 'de'
      ? 'HEARTPORT PARTNER'
      : 'HEARTPORT PARTNER';
  };

  const getDefaultDescription = (): string => {
    return currentLanguage === 'de'
      ? 'Portal f\u00fcr Medical Ambassadors, Branchenvorstellungen und Advisory Honoraria.'
      : 'Portal for Medical Ambassadors, industry introductions, and advisory honoraria.';
  };

  const getDefaultKeywords = (): string => {
    return currentLanguage === 'de'
      ? 'Medical Ambassador, Branchenvorstellungen, Advisory Honorarium, Heartport'
      : 'medical ambassador, industry introductions, advisory honorarium, heartport';
  };

  const ogLocale = currentLanguage === 'de' ? 'de_DE' : 'en_US';
  const ogLocaleAlternate = currentLanguage === 'de' ? 'en_US' : 'de_DE';

  const pathWithoutLang = location.pathname.replace(/^\/[a-z]{2}/, '');
  const canonicalUrl = `${window.location.origin}/${currentLanguage}${pathWithoutLang}${location.search}`;
  const alternateUrlEn = `${window.location.origin}/en${pathWithoutLang}${location.search}`;
  const alternateUrlDe = `${window.location.origin}/de${pathWithoutLang}${location.search}`;

  const absoluteImageUrl = imageUrl.startsWith('http')
    ? imageUrl
    : `${window.location.origin}${imageUrl}`;

  // Compute min-height so a small grey border remains at the bottom
  const [headerHeight, setHeaderHeight] = useState(0);
  useEffect(() => {
    const measure = () => {
      const el = document.getElementById('app-header');
      if (el) setHeaderHeight(el.offsetHeight);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  const bottomBorderPx = 16; // small grey border at bottom
  const contentMinHeight = `calc(100vh - ${headerHeight}px - ${bottomBorderPx}px)`;

  return (
    <div className="min-h-screen text-white bg-[#CAD4DD]">
      <Helmet>
        <html lang={currentLanguage} />
        <title>{title || getDefaultTitle()}</title>
        <meta name="description" content={description || getDefaultDescription()} />
        <meta name="keywords" content={keywords || getDefaultKeywords()} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hrefLang="en" href={alternateUrlEn} />
        <link rel="alternate" hrefLang="de" href={alternateUrlDe} />
        <link rel="alternate" hrefLang="x-default" href={alternateUrlEn} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title || getDefaultTitle()} />
        <meta property="og:description" content={description || getDefaultDescription()} />
        <meta property="og:image" content={absoluteImageUrl} />
        <meta property="og:locale" content={ogLocale} />
        <meta property="og:locale:alternate" content={ogLocaleAlternate} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={title || getDefaultTitle()} />
        <meta name="twitter:description" content={description || getDefaultDescription()} />
        <meta name="twitter:image" content={absoluteImageUrl} />
      </Helmet>

      {/* Header sits on the grey background, not over clouds */}
      <div className="relative z-10">
        <Header />

        {/* White content container with scoped cloud background (no clouds behind header) */}
        <div className="relative bg-white mx-0 lg:mx-4 mb-4 overflow-hidden" style={{ minHeight: contentMinHeight }}>
          <div style={cloudImageStyle}></div>
          {/* Full-container overlay above clouds for consistent readability */}
          <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-[1]"></div>
          <main className="relative z-10 px-4 md:px-8 lg:px-12 py-6">
            <div className="mx-auto max-w-7xl">
              {children}
            </div>
          </main>
        </div>

        {/* Keep cookie banner and footer outside the white container (like public) */}
        <CookieBanner flowerImagePath={flower} />
        <Footer />
      </div>
    </div>
  );
};

export default BaseLayout;
